/**
 * Operations on library drawings (create, save, rename, trash, export, merge…)
 * working on the local store. Server synchronization lives in `sync.ts`.
 */

import {
  exportToBlob,
  reconcileElements,
  serializeAsJSON,
} from "@excalidraw/excalidraw";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import {
  getNonDeletedElements,
  hashElementsVersion,
  isInitializedImageElement,
} from "@excalidraw/element";
import { arrayToMap, getDateTime, randomInteger } from "@excalidraw/common";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";

import { lt } from "./i18n";
import { DrawingStore, emitLibraryChange } from "./store";

import type {
  DrawingId,
  DrawingMeta,
  DrawingScene,
  FolderId,
  LibraryChangeOrigin,
} from "./types";

/** deleted elements are kept for a while so that deletions win when merging
 * with copies edited on other devices */
const DELETED_ELEMENTS_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

const THUMBNAIL_SIZE = 480;

/** appState properties that belong to a drawing (the rest are user
 * preferences, kept per browser) */
const DRAWING_APP_STATE_KEYS = [
  "name",
  "viewBackgroundColor",
  "scrollX",
  "scrollY",
  "zoom",
  "gridSize",
  "gridStep",
  "gridModeEnabled",
  "frameRendering",
] as const;

export const newDrawingId = (): DrawingId => {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
};

export const defaultDrawingName = () => `${lt("untitled")}-${getDateTime()}`;

export const pickDrawingAppState = (
  appState: Partial<AppState> | null | undefined,
): Partial<AppState> => {
  const picked: Record<string, unknown> = {};
  for (const key of DRAWING_APP_STATE_KEYS) {
    if (appState?.[key] !== undefined) {
      picked[key] = appState[key];
    }
  }
  return picked as Partial<AppState>;
};

export const getReferencedFileIds = (
  elements: readonly ExcalidrawElement[],
) => {
  const ids = new Set<string>();
  for (const element of elements) {
    if (!element.isDeleted && isInitializedImageElement(element)) {
      ids.add(element.fileId);
    }
  }
  return [...ids];
};

export const restoreDrawingElements = (
  elements: readonly ExcalidrawElement[] | null | undefined,
) =>
  restoreElements(elements, null, {
    repairBindings: true,
  }) as OrderedExcalidrawElement[];

const buildDrawingScene = (
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
): DrawingScene => {
  const cutoff = Date.now() - DELETED_ELEMENTS_RETENTION_MS;
  return {
    elements: elements.filter(
      (element) => !element.isDeleted || (element.updated ?? 0) > cutoff,
    ),
    appState: pickDrawingAppState(appState),
  };
};

/**
 * Saves the given scene as the content of drawing `id`, creating it if
 * needed. Empty scenes don't create new drawings. Changes to the elements or
 * the name mark the drawing as dirty (to be pushed to the server).
 */
export const saveDrawingContent = async (
  id: DrawingId,
  opts: {
    elements: readonly ExcalidrawElement[];
    appState: Partial<AppState>;
    files: BinaryFiles | null;
    name?: string | null;
    /** folder of a drawing being created */
    folderId?: FolderId | null;
    origin: LibraryChangeOrigin;
  },
) => {
  const scene = buildDrawingScene(opts.elements, opts.appState);
  const elementCount = getNonDeletedElements(opts.elements).length;
  const prev = await DrawingStore.getMeta(id);
  if (!prev && !elementCount) {
    return null;
  }

  const fileIds = getReferencedFileIds(opts.elements);
  const missingFileIds = await DrawingStore.getMissingFileIds(fileIds);
  if (missingFileIds.length && opts.files) {
    await DrawingStore.putFiles(
      missingFileIds
        .map((fileId) => opts.files![fileId])
        .filter((file): file is BinaryFileData => !!file),
    );
  }

  const name =
    opts.name || opts.appState.name || prev?.name || defaultDrawingName();
  const elementsHash = hashElementsVersion(scene.elements);
  const contentChanged =
    !prev ||
    prev.elementsHash !== elementsHash ||
    prev.name !== name ||
    prev.deleted ||
    prev.purged;
  const now = Date.now();

  await DrawingStore.putScene(id, {
    elements: scene.elements,
    appState: { ...scene.appState, name },
  });
  const meta = await DrawingStore.updateMeta(id, (current) => ({
    id,
    createdAt: current?.createdAt ?? now,
    folderId: current ? current.folderId : opts.folderId ?? null,
    metaUpdatedAt:
      !current || current.name !== name || current.deleted
        ? now
        : current.metaUpdatedAt ?? current.updatedAt,
    thumbnail: current?.thumbnail ?? null,
    serverVersion: current?.serverVersion ?? 0,
    name,
    fileIds,
    elementsHash,
    elementCount,
    updatedAt: contentChanged || !current ? now : current.updatedAt,
    deleted: false,
    purged: false,
    dirty: (current?.dirty ?? false) || contentChanged,
    localSavedAt: now,
    conflict: current?.conflict ?? null,
    snapshot: current?.snapshot ?? false,
  }));

  if (contentChanged) {
    emitLibraryChange([id], opts.origin);
  }
  return { meta: meta!, contentChanged };
};

const blobToDataURL = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const toFilesMap = (files: readonly BinaryFileData[]) => {
  const map: BinaryFiles = {};
  for (const file of files) {
    map[file.id] = file;
  }
  return map;
};

const renderImage = (
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  opts: { mimeType: string; maxWidthOrHeight?: number },
) =>
  exportToBlob({
    elements: getNonDeletedElements(elements),
    appState: {
      ...appState,
      exportBackground: true,
      exportWithDarkMode: false,
      viewBackgroundColor:
        appState.viewBackgroundColor ||
        getDefaultAppState().viewBackgroundColor,
    },
    files,
    maxWidthOrHeight: opts.maxWidthOrHeight,
    mimeType: opts.mimeType,
    quality: 0.85,
    exportPadding: 16,
  });

export const generateThumbnail = async (
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
): Promise<string | null> => {
  if (!getNonDeletedElements(elements).length) {
    return null;
  }
  const blob = await renderImage(elements, appState, files, {
    mimeType: "image/webp",
    maxWidthOrHeight: THUMBNAIL_SIZE,
  });
  return blobToDataURL(blob);
};

export const setDrawingThumbnail = async (
  id: DrawingId,
  thumbnail: string | null,
  origin: LibraryChangeOrigin,
) => {
  const meta = await DrawingStore.updateMeta(id, (current) =>
    current && !current.purged && current.thumbnail !== thumbnail
      ? { ...current, thumbnail, dirty: true, localSavedAt: Date.now() }
      : undefined,
  );
  if (meta) {
    emitLibraryChange([id], origin);
  }
};

/** loads a drawing with its files (from the local store) */
export const loadDrawing = async (id: DrawingId) => {
  const [meta, scene] = await Promise.all([
    DrawingStore.getMeta(id),
    DrawingStore.getScene(id),
  ]);
  if (!meta || !scene) {
    return null;
  }
  const files = toFilesMap(await DrawingStore.getFiles(meta.fileIds));
  return { meta, scene, files };
};

/** regenerates the thumbnail from the stored scene */
export const refreshThumbnail = async (
  id: DrawingId,
  origin: LibraryChangeOrigin,
) => {
  const drawing = await loadDrawing(id);
  if (!drawing) {
    return;
  }
  const thumbnail = await generateThumbnail(
    restoreDrawingElements(drawing.scene.elements),
    drawing.scene.appState,
    drawing.files,
  );
  await setDrawingThumbnail(id, thumbnail, origin);
};

export const createDrawing = async (opts: {
  name?: string | null;
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles | null;
  folderId?: FolderId | null;
}) => {
  const id = newDrawingId();
  const result = await saveDrawingContent(id, {
    ...opts,
    origin: "library",
  });
  if (!result) {
    // empty scene: create it anyway, the user explicitly asked for it
    const now = Date.now();
    const name = opts.name || opts.appState.name || defaultDrawingName();
    await DrawingStore.putScene(id, {
      elements: [],
      appState: { ...pickDrawingAppState(opts.appState), name },
    });
    await DrawingStore.updateMeta(id, () => ({
      id,
      name,
      createdAt: now,
      updatedAt: now,
      metaUpdatedAt: now,
      deleted: false,
      purged: false,
      folderId: opts.folderId ?? null,
      thumbnail: null,
      fileIds: [],
      elementsHash: hashElementsVersion([]),
      elementCount: 0,
      serverVersion: 0,
      dirty: true,
      localSavedAt: now,
      conflict: null,
      snapshot: false,
    }));
    emitLibraryChange([id], "library");
  }
  await refreshThumbnail(id, "library");
  return id;
};

export const duplicateDrawing = async (id: DrawingId) => {
  const drawing = await loadDrawing(id);
  if (!drawing) {
    return null;
  }
  return createDrawing({
    name: `${drawing.meta.name} ${lt("copySuffix")}`,
    elements: restoreDrawingElements(
      getNonDeletedElements(drawing.scene.elements),
    ),
    appState: drawing.scene.appState,
    files: drawing.files,
    folderId: drawing.meta.folderId,
  });
};

/** metadata changes; `isContentChange` also updates "last modified" */
const updateDrawingsMeta = async (
  ids: readonly DrawingId[],
  updater: (meta: DrawingMeta) => Partial<DrawingMeta>,
  isContentChange = false,
) => {
  const changed: DrawingId[] = [];
  for (const id of ids) {
    const now = Date.now();
    const meta = await DrawingStore.updateMeta(id, (current) =>
      current
        ? {
            ...current,
            ...updater(current),
            updatedAt: isContentChange ? now : current.updatedAt,
            metaUpdatedAt: now,
            dirty: true,
            localSavedAt: now,
          }
        : undefined,
    );
    if (meta) {
      changed.push(id);
    }
  }
  emitLibraryChange(changed, "library");
};

export const renameDrawing = async (id: DrawingId, name: string) => {
  const scene = await DrawingStore.getScene(id);
  if (scene) {
    await DrawingStore.putScene(id, {
      ...scene,
      appState: { ...scene.appState, name },
    });
  }
  await updateDrawingsMeta([id], () => ({ name }), true);
};

export const trashDrawings = (ids: readonly DrawingId[]) =>
  updateDrawingsMeta(ids, () => ({ deleted: true }));

/** restores from the trash (into the root if their folder was deleted) */
export const restoreDrawings = async (ids: readonly DrawingId[]) => {
  const liveFolders = new Set(
    (await DrawingStore.listFolders())
      .filter((folder) => !folder.deleted)
      .map((folder) => folder.id),
  );
  await updateDrawingsMeta(ids, (meta) => ({
    deleted: false,
    folderId:
      meta.folderId && liveFolders.has(meta.folderId) ? meta.folderId : null,
  }));
};

export const moveDrawings = (
  ids: readonly DrawingId[],
  folderId: FolderId | null,
) => updateDrawingsMeta(ids, () => ({ folderId }));

/** permanently deletes drawings (propagated to the server on next sync) */
export const purgeDrawings = async (ids: readonly DrawingId[]) => {
  for (const id of ids) {
    const meta = await DrawingStore.getMeta(id);
    if (!meta) {
      continue;
    }
    if (meta.serverVersion === 0) {
      await DrawingStore.remove(id);
    } else {
      await DrawingStore.putScene(id, { elements: [], appState: {} });
      await DrawingStore.updateMeta(id, (current) =>
        current
          ? {
              ...current,
              purged: true,
              deleted: true,
              dirty: true,
              thumbnail: null,
              fileIds: [],
              localSavedAt: Date.now(),
            }
          : undefined,
      );
    }
  }
  await DrawingStore.collectGarbageFiles();
  emitLibraryChange([...ids], "library");
};

/** `.excalidraw` JSON, same format as "Save to disk" */
export const serializeDrawing = (drawing: {
  scene: DrawingScene;
  files: BinaryFiles;
}) =>
  serializeAsJSON(
    getNonDeletedElements(restoreDrawingElements(drawing.scene.elements)),
    drawing.scene.appState,
    drawing.files,
    "local",
  );

export const renderDrawingPng = (drawing: {
  scene: DrawingScene;
  files: BinaryFiles;
}) =>
  renderImage(
    restoreDrawingElements(drawing.scene.elements),
    drawing.scene.appState,
    drawing.files,
    { mimeType: "image/png" },
  );

/** element-wise merge of two copies of the same drawing (same algorithm used
 * by live collaboration: higher element versions win) */
export const mergeDrawingScenes = (
  local: DrawingScene,
  remote: DrawingScene,
): DrawingScene => ({
  elements: reconcileElements(
    restoreDrawingElements(local.elements),
    restoreDrawingElements(remote.elements) as RemoteExcalidrawElement[],
    getDefaultAppState() as AppState,
  ),
  appState: local.appState,
});

const isMissingOrDeleted = (element: ExcalidrawElement | undefined) =>
  !element || element.isDeleted;

/** whether two element lists represent the same drawing (deleted elements
 * and missing ones are equivalent) */
export const sameElements = (
  a: readonly ExcalidrawElement[],
  b: readonly ExcalidrawElement[],
) => {
  const mapA = arrayToMap(a);
  const mapB = arrayToMap(b);
  for (const id of new Set([...mapA.keys(), ...mapB.keys()])) {
    const elementA = mapA.get(id);
    const elementB = mapB.get(id);
    if (isMissingOrDeleted(elementA) && isMissingOrDeleted(elementB)) {
      continue;
    }
    if (elementA?.versionNonce !== elementB?.versionNonce) {
      return false;
    }
  }
  return true;
};

/**
 * Returns `target` with element versions raised above those of `other`, and
 * the elements only present in `other` marked deleted, so that when the two
 * copies are merged later (e.g. on another device), `target` wins.
 */
export const makeSceneDominant = (
  target: DrawingScene,
  other: DrawingScene | null | undefined,
): DrawingScene => {
  if (!other) {
    return target;
  }
  const now = Date.now();
  const otherMap = arrayToMap(other.elements);
  const targetIds = new Set(target.elements.map((element) => element.id));
  const elements: ExcalidrawElement[] = target.elements.map((element) => {
    const otherElement = otherMap.get(element.id);
    if (!otherElement || element.version > otherElement.version) {
      return element;
    }
    return {
      ...element,
      version: otherElement.version + 1,
      versionNonce: randomInteger(),
      updated: now,
    };
  });
  for (const otherElement of other.elements) {
    if (!targetIds.has(otherElement.id) && !otherElement.isDeleted) {
      elements.push({
        ...otherElement,
        isDeleted: true,
        version: otherElement.version + 1,
        versionNonce: randomInteger(),
        updated: now,
      });
    }
  }
  return { elements, appState: target.appState };
};
